import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BugIcon } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BugReportButton = () => {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [type, setType] = useState('bug'); // 'bug', 'feature', 'improvement'
  
  const location = useLocation();
  const { user } = useAuth();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post('/api/bug-reports', {
        description,
        type,
        path: location.pathname,
        userID: user?.id || null,
        userEmail: user?.email || null,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
      
      setFeedbackMessage('Thank you! Your report has been submitted.');
      setDescription('');
      setType('bug');
      
      // Close the modal after 3 seconds
      setTimeout(() => {
        setShowModal(false);
        setFeedbackMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      setFeedbackMessage('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <button 
        className="btn btn-primary rounded-circle position-fixed"
        style={{ 
          bottom: '20px', 
          right: '20px', 
          width: '50px', 
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1030
        }}
        onClick={() => setShowModal(true)}
        title="Report an issue or request a feature"
      >
        <BugIcon size={24} />
      </button>
      
      {showModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Report an Issue</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowModal(false);
                      setFeedbackMessage('');
                    }}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {feedbackMessage ? (
                      <div className={`alert ${feedbackMessage.includes('Failed') ? 'alert-danger' : 'alert-success'}`}>
                        {feedbackMessage}
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Type</label>
                          <select 
                            className="form-select"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            required
                          >
                            <option value="bug">Bug Report</option>
                            <option value="feature">Feature Request</option>
                            <option value="improvement">Improvement Suggestion</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows="5"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Please describe the issue or feature request in detail..."
                            required
                          ></textarea>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">
                            Page: {location.pathname}<br />
                            {user ? `User: ${user.email || user.username}` : 'Not logged in'}
                          </small>
                        </div>
                      </>
                    )}
                  </div>
                  {!feedbackMessage && (
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Submitting...
                          </>
                        ) : (
                          'Submit Report'
                        )}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BugReportButton;